import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import { selectAllPnlHistory } from 'src/store/bots'
import clsx from 'clsx'

// --- TYPE DEFINITIONS ---
interface StatCardProps {
  title: string
  children: React.ReactNode
  className?: string
}

interface DailyHistoryEntry { pnl: number | string }
interface BotData {
  yearAgo?: { totals?: Record<string, number | string> }
  total?: { winning?: number; losing?: number; breakeven?: number; winRate?: number | string }
  dailyHistory?: DailyHistoryEntry[]
}

// --- REUSABLE CARD COMPONENT ---
const StatCard: React.FC<StatCardProps> = ({ title, children, className = '' }) => (
  <div
    className={clsx(
      'bg-[#1F1F1F] rounded-lg border border-slate-700/60 flex flex-col p-5 relative overflow-hidden',
      className,
    )}
  >
    <h3 className="text-slate-400 text-sm font-medium mb-4">{title}</h3>
    <div className="flex-grow flex flex-col justify-center">{children}</div>
  </div>
)

// --- MAIN COMPONENT ---
export default function PnLInsights() {
  const pnlHistory = useSelector(selectAllPnlHistory, _.isEqual)

  const { topExchange, botSummary, dailyTrend, contribution, cumulativePnL, trendTextY, equityTextY } = useMemo(() => {
    let topExchange = { name: '-', pnl: 0 }
    const contribution: Record<string, number> = {}
    let botSummary = { winning: 0, losing: 0, breakeven: 0, winRate: 0 }
    let dailyTrend: number[] = []
    let cumulativePnL: number[] = []
    let trendTextY = 50
    let equityTextY = 50

    const all = Object.values(pnlHistory || {})
    if (all.length > 0) {
      const botData: BotData = all[0] as BotData
      const yearAgoTotals = botData?.yearAgo?.totals || {}

      const totalYearPnl = Object.values(yearAgoTotals).reduce(
        (sum: number, val) => sum + (Number(val) || 0),
        0,
      )

      for (const [ex, val] of Object.entries(yearAgoTotals)) {
        const pnl = Number(val) || 0
        contribution[ex] = totalYearPnl !== 0 ? (pnl / totalYearPnl) * 100 : 0
      }

      const sorted = Object.entries(yearAgoTotals).sort((a, b) => Number(b[1]) - Number(a[1]))
      if (sorted.length) topExchange = { name: sorted[0][0], pnl: Number(sorted[0][1]) }
      if (botData?.total) {
        const winRateValue = Number(botData.total.winRate)
        botSummary = {
          winning: botData.total.winning ?? 0,
          losing: botData.total.losing ?? 0,
          breakeven: botData.total.breakeven ?? 0,
          winRate: !isNaN(winRateValue) ? winRateValue : 0,
        }
      }

      dailyTrend = (botData?.dailyHistory ?? []).slice(-7).map((d) => Number(d.pnl ?? 0))
      let runningTotal = 0
      if (botData?.dailyHistory?.length) {
        cumulativePnL = botData.dailyHistory.map((d) => {
          runningTotal += Number(d.pnl ?? 0)
          return runningTotal
        })
      }

      // --- FIX: PNL Trend Text Position Calculation ---
      const lastTrendValue = dailyTrend.at(-1)
      if (lastTrendValue !== undefined && dailyTrend.length > 1) {
        const maxAbs = Math.max(...dailyTrend.map(Math.abs)) || 1
        const lastPointY = 50 - (lastTrendValue / maxAbs) * 25
        // offset text slightly away from the line
        const offset = lastTrendValue >= 0 ? -8 : 8
        trendTextY = Math.min(90, Math.max(10, lastPointY + offset))
      }

      // --- FIX: Equity Curve Text Position Calculation ---
      const lastEquityValue = cumulativePnL.at(-1)
      if (lastEquityValue !== undefined && cumulativePnL.length > 1) {
        const equityMax = Math.max(0, ...cumulativePnL)
        const equityMin = Math.min(0, ...cumulativePnL)
        const equityRange = equityMax - equityMin
        const scaleFactor = equityRange !== 0 ? 50 / equityRange : 1
        const lastPointY = 50 - (lastEquityValue - equityMin) * scaleFactor
        // offset text away from the line
        const offset = lastEquityValue >= 0 ? -10 : 10
        equityTextY = Math.min(90, Math.max(10, lastPointY + offset))
      }
    }

    return { topExchange, contribution, botSummary, dailyTrend, cumulativePnL, trendTextY, equityTextY }
  }, [pnlHistory])

  const equityMax = cumulativePnL.length ? Math.max(0, ...cumulativePnL) : 1
  const equityMin = cumulativePnL.length ? Math.min(0, ...cumulativePnL) : 0
  const equityRange = equityMax - equityMin
  const scaleFactor = equityRange !== 0 ? 50 / equityRange : 1
  const lastEquityValue = cumulativePnL.at(-1)

  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
      {/* --- Top Exchange --- */}
      <StatCard title="Top Exchange (Yearly)">
        <p className="text-2xl font-semibold text-cyan-400 mb-1">{topExchange.name || 'N/A'}</p>
        <p
          className={clsx(
            'text-3xl font-bold',
            topExchange.pnl >= 0 ? 'text-emerald-400' : 'text-red-400',
          )}
        >
          {topExchange.pnl >= 0 ? '+' : ''}
          {topExchange.pnl.toFixed(2)}
          <span className="text-base ml-2 font-normal text-slate-500">USDT</span>
        </p>
      </StatCard>

      {/* --- Trade Summary --- */}
      <StatCard title="Trade Summary">
        <div className="space-y-2 text-base">
          <div className="flex justify-between items-baseline">
            <span className="text-slate-300">Winning</span>
            <span className="font-medium text-emerald-400">{botSummary.winning}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-slate-300">Losing</span>
            <span className="font-medium text-red-400">{botSummary.losing}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-slate-300">Breakeven</span>
            <span className="font-medium text-yellow-400">{botSummary.breakeven}</span>
          </div>
          <div className="flex justify-between items-baseline border-t border-slate-700/60 pt-2 mt-2">
            <span className="text-slate-300">Win Rate</span>
            <span className="font-bold text-xl text-cyan-400">{botSummary.winRate.toFixed(1)}%</span>
          </div>
        </div>
      </StatCard>

      {/* --- Exchange Contribution --- */}
      <StatCard title="Exchange Contribution (Yearly)">
        <div className="space-y-3">
          {Object.keys(contribution).length > 0 ? (
            Object.entries(contribution)
              .sort(([, a], [, b]) => b - a)
              .map(([ex, pct]) => (
                <div key={ex}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-slate-300">{ex}</span>
                    <span className="text-slate-400">{pct.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-slate-700/50 rounded-full h-1.5">
                    <div
                      className="bg-cyan-400 h-1.5 rounded-full"
                      style={{ width: `${Math.max(0, pct)}%` }}
                    ></div>
                  </div>
                </div>
              ))
          ) : (
            <p className="text-slate-500 text-sm h-full flex items-center justify-center">
              No contribution data
            </p>
          )}
        </div>
      </StatCard>

      {/* --- PnL Trend Card --- */}
      <StatCard title="PnL Trend (Last 7 Days)">
        <div className="relative w-full h-full min-h-[80px]">
          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
              </linearGradient>
            </defs>
            {dailyTrend.length > 1 && (
              <>
                <polygon
                  fill="url(#trendFill)"
                  points={`0,50 ${dailyTrend
                    .map(
                      (v, i) =>
                        `${(i / (dailyTrend.length - 1)) * 100},${50 - (v / (Math.max(...dailyTrend.map(Math.abs)) || 1)) * 25}`,
                    )
                    .join(' ')} 100,50`}
                />
                <polyline
                  fill="none"
                  stroke="#22d3ee"
                  strokeWidth="2"
                  points={dailyTrend
                    .map(
                      (v, i) =>
                        `${(i / (dailyTrend.length - 1)) * 100},${50 - (v / (Math.max(...dailyTrend.map(Math.abs)) || 1)) * 25}`,
                    )
                    .join(' ')}
                />
              </>
            )}
          </svg>
          {dailyTrend.length > 0 && dailyTrend.at(-1) !== undefined && (
            <div
              className="absolute right-2 transition-all duration-300 ease-out"
              style={{ top: `${trendTextY}%` }}
            >
              <p
                className={clsx(
                  'text-sm font-medium px-2 py-0.5 rounded-md backdrop-blur-sm bg-black/30',
                  dailyTrend.at(-1)! >= 0 ? 'text-emerald-400' : 'text-red-400',
                )}
              >
                {dailyTrend.at(-1)! >= 0 ? '+' : ''}
                {dailyTrend.at(-1)!.toFixed(2)}
              </p>
            </div>
          )}
        </div>
      </StatCard>

      {/* --- Equity Curve Card --- */}
      <StatCard title="Equity Curve" className="md:col-span-2 xl:col-span-4">
        <div className="relative w-full h-36">
          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
              </linearGradient>
            </defs>
            {cumulativePnL.length > 1 && (
              <>
                <polygon
                  fill="url(#equityFill)"
                  points={`0,50 ${cumulativePnL
                    .map(
                      (val, i) =>
                        `${(i / (cumulativePnL.length - 1)) * 100},${50 - (val - equityMin) * scaleFactor}`,
                    )
                    .join(' ')} 100,50`}
                />
                <polyline
                  fill="none"
                  stroke="#22d3ee"
                  strokeWidth="2"
                  points={cumulativePnL
                    .map(
                      (val, i) =>
                        `${(i / (cumulativePnL.length - 1)) * 100},${50 - (val - equityMin) * scaleFactor}`,
                    )
                    .join(' ')}
                />
              </>
            )}
          </svg>
          {lastEquityValue !== undefined && (
            <div
              className="absolute right-5 text-right transition-all duration-300 ease-out"
              style={{ top: `${equityTextY}%` }}
            >
              <div className="px-2 py-1 rounded-md backdrop-blur-sm bg-black/30">
                <p className="text-2xl font-bold text-slate-100">
                  {lastEquityValue.toFixed(2)} USDT
                </p>
                <p className="text-sm text-slate-400">Current Equity</p>
              </div>
            </div>
          )}
        </div>
      </StatCard>
    </div>
  )
}
